import { CalendarClock, CreditCard, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { getChoiceOptions } from '@/lib/form-builder/field-validation';
import type { FormField } from '@/types/form-definition';
import { cn } from '@/lib/utils';

function showLabel(field: FormField) {
  return field.questionProperties?.general?.labelVisible !== false;
}

function FieldLabel({ field, className }: { field: FormField; className?: string }) {
  if (!showLabel(field)) return null;
  const text = field.label?.trim() || field.type;
  return (
    <Label className={cn('text-sm font-medium', className)}>
      {text}
      {field.questionProperties?.general?.required ? (
        <span className="ml-0.5 text-destructive" aria-hidden>
          *
        </span>
      ) : null}
    </Label>
  );
}

function Hint({ field }: { field: FormField }) {
  const h = field.hint?.trim();
  if (!h) return null;
  return <p className="text-xs text-muted-foreground">{h}</p>;
}

export function FieldCanvasPreview({ field }: { field: FormField }) {
  const ph = field.placeholder?.trim() || undefined;
  const labelCls = 'mb-1.5 block';

  switch (field.type) {
    case 'header':
      return (
        <div className="space-y-1">
          <h3 className="text-lg font-semibold tracking-tight">{field.label?.trim() || 'Heading'}</h3>
        </div>
      );

    case 'paragraph': {
      const text = (field.html ?? '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      return (
        <p className="text-sm leading-relaxed text-muted-foreground">
          {text || 'Paragraph text'}
        </p>
      );
    }

    case 'richText': {
      const text = (field.html ?? '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      return (
        <p className="text-sm leading-relaxed text-muted-foreground">
          {text || 'Rich text content'}
        </p>
      );
    }

    case 'divider':
      return <Separator className="my-1" />;

    case 'pageBreak':
      return (
        <div className="flex items-center gap-2 py-2">
          <div className="h-px flex-1 bg-border" />
          <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            Page break
          </span>
          <div className="h-px flex-1 bg-border" />
        </div>
      );

    case 'submit':
      return (
        <Button type="button" disabled className="pointer-events-none w-full max-w-xs sm:w-auto">
          {field.label?.trim() || 'Submit'}
        </Button>
      );

    case 'shortText':
      return (
        <div className="space-y-1">
          <FieldLabel field={field} className={labelCls} />
          <Input placeholder={ph} disabled className="bg-background/80" />
          <Hint field={field} />
        </div>
      );

    case 'longText':
      return (
        <div className="space-y-1">
          <FieldLabel field={field} className={labelCls} />
          <Textarea placeholder={ph} disabled rows={3} className="resize-none bg-background/80" />
          <Hint field={field} />
        </div>
      );

    case 'email':
      return (
        <div className="space-y-1">
          <FieldLabel field={field} className={labelCls} />
          <Input type="email" placeholder={ph ?? 'email@example.com'} disabled className="bg-background/80" />
          <Hint field={field} />
        </div>
      );

    case 'phone':
      return (
        <div className="space-y-1">
          <FieldLabel field={field} className={labelCls} />
          <Input type="tel" placeholder={ph ?? '(555) 000-0000'} disabled className="bg-background/80" />
          <Hint field={field} />
        </div>
      );

    case 'number':
      return (
        <div className="space-y-1">
          <FieldLabel field={field} className={labelCls} />
          <Input type="number" placeholder={ph ?? '0'} disabled className="bg-background/80" />
          <Hint field={field} />
        </div>
      );

    case 'datePicker':
      return (
        <div className="space-y-1">
          <FieldLabel field={field} className={labelCls} />
          <Input type="date" disabled className="max-w-[220px] bg-background/80" />
          <Hint field={field} />
        </div>
      );

    case 'time':
      return (
        <div className="space-y-1">
          <FieldLabel field={field} className={labelCls} />
          <Input type="time" disabled className="max-w-[160px] bg-background/80" />
          <Hint field={field} />
        </div>
      );

    case 'dropdown': {
      const opts = getChoiceOptions(field);
      const first = opts.find((o) => !o.disabled)?.value ?? opts[0]?.value ?? '';
      return (
        <div className="space-y-1">
          <FieldLabel field={field} className={labelCls} />
          <Select disabled value={first || undefined}>
            <SelectTrigger className="max-w-md bg-background/80">
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent>
              {opts.map((o, i) => (
                <SelectItem key={`${field.id}-dd-${i}-${o.value}`} value={o.value} disabled={o.disabled}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Hint field={field} />
        </div>
      );
    }

    case 'singleChoice': {
      const opts = getChoiceOptions(field);
      const first = opts.find((o) => !o.disabled)?.value ?? opts[0]?.value ?? 'option';
      return (
        <div className="space-y-2">
          <FieldLabel field={field} className={labelCls} />
          <RadioGroup value={first} disabled className="gap-2">
            {opts.map((o, i) => (
              <div key={`${field.id}-sc-${i}-${o.value}`} className="flex items-center gap-2">
                <RadioGroupItem value={o.value} id={`pv-${field.id}-${i}-${o.value}`} disabled={o.disabled} />
                <Label htmlFor={`pv-${field.id}-${i}-${o.value}`} className="font-normal text-muted-foreground">
                  {o.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
          <Hint field={field} />
        </div>
      );
    }

    case 'multipleChoice': {
      const opts = getChoiceOptions(field);
      return (
        <div className="space-y-2">
          <FieldLabel field={field} className={labelCls} />
          <div className="space-y-2">
            {opts.map((o, i) => (
              <div key={`${field.id}-mc-${i}-${o.value}`} className="flex items-center gap-2">
                <Checkbox
                  id={`pv-${field.id}-${i}-${o.value}`}
                  disabled
                  checked={false}
                  className="pointer-events-none"
                />
                <Label htmlFor={`pv-${field.id}-${i}-${o.value}`} className="font-normal text-muted-foreground">
                  {o.label}
                </Label>
              </div>
            ))}
          </div>
          <Hint field={field} />
        </div>
      );
    }

    case 'fullName':
      return (
        <div className="space-y-2">
          <FieldLabel field={field} className={labelCls} />
          <div className="grid gap-2 sm:grid-cols-2">
            <Input placeholder="First name" disabled className="bg-background/80" />
            <Input placeholder="Last name" disabled className="bg-background/80" />
          </div>
          <Hint field={field} />
        </div>
      );

    case 'address':
      return (
        <div className="space-y-2">
          <FieldLabel field={field} className={labelCls} />
          <Input placeholder="Street address" disabled className="bg-background/80" />
          <div className="grid gap-2 sm:grid-cols-2">
            <Input placeholder="City" disabled className="bg-background/80" />
            <Input placeholder="ZIP" disabled className="bg-background/80" />
          </div>
          <Hint field={field} />
        </div>
      );

    case 'signature':
      return (
        <div className="space-y-1">
          <FieldLabel field={field} className={labelCls} />
          <div className="flex h-24 max-w-md items-center justify-center rounded-md border border-dashed bg-muted/30 text-sm text-muted-foreground">
            Sign here
          </div>
          <Hint field={field} />
        </div>
      );

    case 'fileUpload':
      return (
        <div className="space-y-1">
          <FieldLabel field={field} className={labelCls} />
          <Button type="button" variant="outline" disabled className="pointer-events-none gap-2">
            <Upload className="h-4 w-4" />
            Choose file
          </Button>
          <Hint field={field} />
        </div>
      );

    case 'appointment':
      return (
        <div className="space-y-1">
          <FieldLabel field={field} className={labelCls} />
          <div className="flex max-w-md items-center gap-2 rounded-md border border-dashed bg-muted/20 px-3 py-4 text-sm text-muted-foreground">
            <CalendarClock className="h-5 w-5 shrink-0" />
            <span>Select a date and time (configure schedule in field properties)</span>
          </div>
          <Hint field={field} />
        </div>
      );

    case 'payment': {
      const cfg = field.config && typeof field.config === 'object' && !Array.isArray(field.config) ? field.config : {};
      const gateway = typeof (cfg as Record<string, unknown>).gateway === 'string' ? (cfg as Record<string, unknown>).gateway : 'square';
      const amount = (cfg as Record<string, unknown>).amount;
      const amountSource = typeof (cfg as Record<string, unknown>).amountSource === 'string' ? (cfg as Record<string, unknown>).amountSource : 'fixed';
      const summary =
        amountSource === 'productList'
          ? 'Product list total'
          : typeof amount === 'number'
            ? `$${amount.toFixed(2)}`
            : 'Amount';
      return (
        <div className="space-y-1">
          <FieldLabel field={field} className={labelCls} />
          <div className="flex max-w-md items-center gap-2 rounded-md border border-dashed bg-muted/20 px-3 py-3 text-sm text-muted-foreground">
            <CreditCard className="h-5 w-5 shrink-0" />
            <span>
              {String(gateway).toUpperCase()} · {summary}
            </span>
          </div>
          <Hint field={field} />
        </div>
      );
    }

    default:
      return (
        <div className="text-sm text-muted-foreground">
          <span className="font-mono text-xs">{field.type}</span>
          {field.label ? <span className="ml-2">{field.label}</span> : null}
        </div>
      );
  }
}
